/**
 * The client's copy of his active promises, and the roast that fires when a
 * log entry contradicts one.
 *
 * The server owns the promises (`glitime_memories`, extracted from chat);
 * this module caches the active ones per account in Dexie meta so the
 * broken-promise toast can fire at log time with the phone in airplane mode.
 * The cache refreshes on boot and after every chat close — promises are made
 * in conversation, so those are the only two moments the set can change.
 *
 * Matching is deliberately dumb: the shared food-class taxonomy where both
 * sides have one, a folded-substring look where they don't. A promise about
 * "saldumynai" catches a šokoladukas through the class; a promise about
 * "kebabai" catches "Kebabas su vištiena" through the substring. Anything
 * cleverer would need to be right about Lithuanian morphology to be better.
 */

import { classifyFoodName, foldLt } from '@gramas/shared'
import { api } from '../api/client.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { sassLine } from '../sass/engine.js'
import { currentProfile } from '../screens/useProfile.js'
import { showSassToast } from '../shell/toastStore.js'

export interface CachedPromise {
  id: string
  text: string
  subject: string | null
  foodClass: string | null
  declaredOn: string
}

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function metaKey(owner: string): string {
  return `glitime.promises.${owner}`
}
function firedKey(owner: string): string {
  return `glitime.promiseFired.${owner}`
}

const cache = new Map<string, CachedPromise[]>()
const fired = new Map<string, string[]>()
const hydrationPromises = new Map<string, Promise<void>>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = Promise.all([
      getMeta<CachedPromise[]>(metaKey(owner), []),
      getMeta<string[]>(firedKey(owner), []),
    ]).then(([promises, firedIds]) => {
      if (!cache.has(owner)) cache.set(owner, promises)
      if (!fired.has(owner)) fired.set(owner, firedIds)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

/** Pull the server's current set into the cache. Fire-and-forget friendly. */
export async function refreshPromises(): Promise<void> {
  const owner = ownerKey()
  if (owner === ANON_OWNER) return
  try {
    const wire = await api.get<{ promises: CachedPromise[] }>('/api/ai/promises')
    cache.set(owner, wire.promises)
    await setMeta(metaKey(owner), wire.promises)
    // Ids the server no longer lists have been resolved; the guard can shrink.
    const live = new Set(wire.promises.map((p) => p.id))
    const kept = (fired.get(owner) ?? []).filter((id) => live.has(id))
    fired.set(owner, kept)
    await setMeta(firedKey(owner), kept)
  } catch {
    // Offline or signed out: the cached copy keeps working, which is the point.
  }
}

/**
 * The cached active promises, for a UI that just wants to list them (the her
 * zone panel's Pažadai sheet). Answers from the local cache so opening the
 * sheet never waits on the network, and kicks a background refresh so the
 * next open is current.
 */
export async function listActivePromises(): Promise<CachedPromise[]> {
  const owner = ownerKey()
  await hydrate(owner)
  void refreshPromises()
  return cache.get(owner) ?? []
}

/** The first active promise this entry contradicts, or null. */
export function matchPromise(itemName: string | null | undefined): CachedPromise | null {
  const owner = ownerKey()
  void hydrate(owner)
  const name = itemName?.trim()
  if (!name) return null
  const promises = cache.get(owner) ?? []
  if (promises.length === 0) return null

  const folded = foldLt(name)
  const foodClass = classifyFoodName(name)
  const firedIds = fired.get(owner) ?? []

  for (const promise of promises) {
    if (firedIds.includes(promise.id)) continue
    if (promise.foodClass != null && promise.foodClass === foodClass) return promise
    if (
      promise.subject != null &&
      promise.subject.length >= 4 &&
      (folded.includes(promise.subject) || promise.subject.includes(folded))
    ) {
      return promise
    }
  }
  return null
}

/**
 * The roast, with the receipts. Called from the log-toast choke point AFTER
 * the ordinary toast decision, so a broken promise speaks even on a log the
 * 2-in-3 coin would have let pass — her memory outranks her whims.
 */
export function maybePromiseToast(itemName: string | null | undefined): boolean {
  const promise = matchPromise(itemName)
  if (promise == null) return false

  const owner = ownerKey()
  const line = sassLine('promise_broken', {
    promiseText: promise.text,
    promiseDate: promise.declaredOn,
    itemName: itemName ?? undefined,
  })
  if (line == null) return false

  showSassToast(line)
  // Guard first, resolve second: the toast has fired, and a second device
  // repeating the roast because this request is still in flight would be
  // worse than a resolve that arrives twice (the server takes the first).
  const firedIds = [...(fired.get(owner) ?? []), promise.id]
  fired.set(owner, firedIds)
  void setMeta(firedKey(owner), firedIds)
  cache.set(
    owner,
    (cache.get(owner) ?? []).filter((p) => p.id !== promise.id),
  )
  void setMeta(metaKey(owner), cache.get(owner) ?? [])
  void api.post(`/api/ai/promises/${promise.id}/resolve`, { status: 'broken' }).catch(() => {
    // Offline: the fired-guard holds locally; the server learns on the next
    // refresh cycle that never happened — acceptable for a household of two.
  })
  return true
}

/** Test-only: drop the caches so the next read starts cold. */
export function __resetPromisesForTests(): void {
  cache.clear()
  fired.clear()
  hydrationPromises.clear()
}
